import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToastWrapper } from "@/hooks/useToastWrapper";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useContactRecovery } from "@/hooks/useContactRecovery";
import { Mail, Phone, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const ContactForm = () => {
  const { toast } = useToastWrapper();
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const { hasPendingContacts } = useContactRecovery();
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !email || !message) {
      toast("Täytä kaikki pakolliset kentät.");
      return;
    }
    
    if (!acceptTerms) {
      toast("Hyväksy käyttöehdot jatkaaksesi.");
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Track form submission
      trackEvent("contact_form_submitted", {
        name_provided: Boolean(name),
        email_provided: Boolean(email),
        phone_provided: Boolean(phone),
        marketing_accepted: acceptMarketing,
      });
      
      // Use Supabase function endpoint instead of relative path
      const response = await supabase.functions.invoke('send-contact-email', {
        body: {
          name,
          email,
          phone,
          message,
          acceptMarketing,
        },
      });
      
      if (response.error) {
        throw new Error(response.error.message || 'Viestin lähettäminen epäonnistui');
      }
      
      // Ohjaa kiitossivulle onnistuneen lähetyksen jälkeen
      navigate('/kiitos?source=contact');
      
    } catch (error) {
      console.error("Contact form submission error:", error);
      toast("Viestin lähettäminen epäonnistui. Yritä myöhemmin uudelleen.");
      setIsLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-blue-100 p-6 md:p-8">
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-blue-800">Lähetä viesti</h2>
        
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Nimi *
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Sähköposti *
          </label>
          <div className="relative">
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
          </div>
        </div>
        
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Puhelinnumero (valinnainen)
          </label>
          <div className="relative">
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
          </div>
        </div>
        
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            Viesti *
          </label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            rows={5}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div className="space-y-3">
          <div className="flex items-start">
            <input
              id="acceptTerms"
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              required
              className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <label htmlFor="acceptTerms" className="ml-2 block text-sm text-gray-700">
              Hyväksyn{" "}
              <a href="/kayttoehdot" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                käyttöehdot
              </a>{" "}
              ja{" "}
              <a href="/tietosuojaseloste" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                tietosuojaselosteen
              </a>
              . *
            </label>
          </div>
          
          <div className="flex items-start">
            <input
              id="acceptMarketing"
              type="checkbox"
              checked={acceptMarketing}
              onChange={(e) => setAcceptMarketing(e.target.checked)}
              className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <label htmlFor="acceptMarketing" className="ml-2 block text-sm text-gray-700">
              Haluan vastaanottaa ajankohtaisia tietoja ja tarjouksia sähköpostiini.
            </label>
          </div>
        </div>
        
        <Button
          type="submit"
          disabled={isLoading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-md transition-colors flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>Lähetetään...</>
          ) : (
            <>
              <Send className="h-4 w-4" />
              Lähetä viesti
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default ContactForm;
